import React from "react";
import { Link } from "react-router-dom";
import { useRankings } from "../apiCalls/userApiCalls";
import { useStats } from "../apiCalls/statsApiCalls";
import { Container, Button, CountryLabel, Spinner } from "./ui";
import { UsersIcon, GlobeIcon, TrophyIcon } from "./ui/icons";
import { formatNumber } from "../utils";

// The podium: #2, #1, #3 with the leader raised, built from the real top three
// rather than hardcoded names.
const PodiumCard = ({ player, position }) => {
  const accent =
    position === 1
      ? "bg-gold text-navy"
      : position === 2
      ? "bg-silver text-navy"
      : "bg-bronze text-white";

  return (
    <Link
      to={`/players/${player.slug || player._id}`}
      className={`relative block overflow-hidden rounded-md border border-white/80 bg-surface shadow-card transition hover:-translate-y-1 hover:shadow-card-hover ${
        position === 1 ? "z-10" : ""
      }`}
    >
      <span
        className={`absolute left-0 top-0 z-10 flex h-8 w-8 items-center justify-center rounded-br-md text-sm font-bold ${accent}`}
      >
        {position}
      </span>
      <div className={position === 1 ? "aspect-[3/4]" : "aspect-square"}>
        <img
          src={
            player.imgUrl ||
            `${process.env.PUBLIC_URL}/default-profile-image.jpg`
          }
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `${process.env.PUBLIC_URL}/default-profile-image.jpg`;
          }}
        />
      </div>
      <div className={`p-3 text-center ${position === 1 ? "pb-4" : ""}`}>
        <div
          className={`truncate font-semibold text-navy ${
            position === 1 ? "text-base" : "text-sm"
          }`}
        >
          {player.name}
        </div>
        <CountryLabel
          code={player.nationality}
          className="mt-1 justify-center text-muted"
        />
        <div
          className={`tabular mt-2 font-bold text-navy ${
            position === 1 ? "text-xl" : "text-base"
          }`}
        >
          {formatNumber(player.rankingPoints)}{" "}
          <span className="text-xs font-medium text-muted">pts</span>
        </div>
      </div>
    </Link>
  );
};

const Hero = () => {
  const { data, isFetching } = useRankings(1, "", "", "", 3);
  const { data: statsRes } = useStats();

  const top = data?.data ?? [];
  const stats = statsRes?.data;

  return (
    <section className="relative isolate overflow-hidden bg-white">
      {/*
        The court photograph, deliberately under-exposed by the layers on top of
        it. The headline is navy on this, so the image is never allowed to carry
        its full contrast — `opacity` knocks it back against white, and the two
        gradients do the rest.

        `object-[72%_center]` holds the framing on the court and fencing at the
        right of the shot, which is where the podium sits; a centred crop puts
        the empty sky behind the cards instead.
      */}
      <img
        src={`${process.env.PUBLIC_URL}/hero-court.jpg`}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover object-[72%_center] opacity-40 lg:opacity-[0.55]"
      />

      {/* Left-to-right wash: near-solid under the copy, thinning towards the
          photograph so the court is still legible behind the podium. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-white via-white/85 to-white/45"
      />

      {/* Vertical fade, which both lifts the whole panel and lands it on the
          page background instead of ending at a hard edge. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-white/70 via-transparent to-bg"
      />

      <Container className="relative py-16 md:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary backdrop-blur-sm">
              <GlobeIcon />
              World rankings · Men &amp; Women
            </span>

            <h1 className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl xl:text-6xl">
              The Global
              <br />
              <span className="text-primary">Padel Ranking</span>
              <br />
              Platform
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-navy/70">
              Track your performance, follow the world's best tournaments, and
              discover the global padel rankings. Compete. Climb. Be recognized.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/rankings" size="lg">
                Explore Rankings
              </Button>
              <Button to="/register" variant="secondary" size="lg">
                Get Ranked
              </Button>
            </div>

            {/* The stats sit on the photograph, so they get their own surface
                rather than relying on the wash alone. */}
            <dl className="mt-10 inline-flex flex-wrap gap-x-10 gap-y-6 rounded-md border border-white/70 bg-white/70 px-6 py-5 backdrop-blur-sm">
              {[
                {
                  icon: <UsersIcon />,
                  value: stats?.rankedPlayers,
                  label: "Players",
                },
                {
                  icon: <GlobeIcon />,
                  value: stats?.countries,
                  label: "Countries",
                },
                {
                  icon: <TrophyIcon />,
                  value: stats?.tournaments,
                  label: "Tournaments",
                },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <span className="text-primary">{stat.icon}</span>
                  <div>
                    <dd className="tabular text-xl font-bold text-primary">
                      {formatNumber(stat.value ?? 0)}
                    </dd>
                    <dt className="text-xs font-medium text-muted">
                      {stat.label}
                    </dt>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative">
            {isFetching && top.length === 0 ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : top.length >= 3 ? (
              <div className="grid grid-cols-3 items-end gap-3 sm:gap-4">
                <PodiumCard player={top[1]} position={2} />
                <PodiumCard player={top[0]} position={1} />
                <PodiumCard player={top[2]} position={3} />
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Hero;
